import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
import * as Y from "yjs";
import { HocuspocusProvider } from "@hocuspocus/provider";

function getApiHost() {
  let host = import.meta.env.VITE_APP_HOST;
  if (window.location.hostname == "devtest.subangstrom.com") {
    host = "https://nodedev.subangstrom.com";
  }
  return host;
}

function toWsUrl(apiHost) {
  const url = new URL(apiHost);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/yjs";
  return url.toString();
}

async function fetchYjsSession() {
  const response = await fetch(`${getApiHost()}/api/v1/yjs/token`, {
    credentials: "include",
    method: "POST",
    headers: { "content-type": "application/json" },
  });
  const payload = await response.json();
  if (!response.ok || payload.status === "fail" || payload.status === "error") {
    throw new Error(payload.message || "Failed to create Yjs session");
  }
  return payload.data;
}

export const useYjsProviderStore = defineStore("yjsProvider", () => {
  const ydoc = shallowRef(null);
  const provider = shallowRef(null);
  const connectionState = ref("disconnected");
  const isSynced = ref(false);
  const currentLabId = ref(null);
  const documentName = ref(null);
  const sessionData = ref(null);
  const error = ref(null);

  async function disconnect() {
    if (provider.value) {
      provider.value.destroy();
      provider.value = null;
    }
    if (ydoc.value) {
      ydoc.value.destroy();
      ydoc.value = null;
    }
    connectionState.value = "disconnected";
    isSynced.value = false;
    currentLabId.value = null;
    documentName.value = null;
    sessionData.value = null;
  }

  async function connect() {
    const session = await fetchYjsSession();
    const currentSession = sessionData.value;
    const canReuseProvider =
      provider.value &&
      currentLabId.value === session.labId &&
      documentName.value === session.documentName &&
      currentSession?.userId === session.userId &&
      currentSession?.access === session.access &&
      currentSession?.grantsVersion === session.grantsVersion;

    if (canReuseProvider) {
      return session;
    }

    await disconnect();

    error.value = null;
    connectionState.value = "connecting";
    isSynced.value = false;
    currentLabId.value = session.labId;
    documentName.value = session.documentName;
    sessionData.value = session;

    const doc = new Y.Doc();
    const hocuspocus = new HocuspocusProvider({
      url: toWsUrl(getApiHost()),
      name: session.documentName,
      document: doc,
      token: session.token,
      onConnect: () => {
        connectionState.value = "connected";
      },
      onDisconnect: () => {
        connectionState.value = "disconnected";
        isSynced.value = false;
      },
      onSynced: () => {
        connectionState.value = "synced";
        isSynced.value = true;
      },
      onAuthenticationFailed: ({ reason }) => {
        error.value = reason || "Yjs authentication failed";
        connectionState.value = "disconnected";
        isSynced.value = false;
      },
    });

    ydoc.value = doc;
    provider.value = hocuspocus;
    return session;
  }

  return {
    ydoc,
    provider,
    connectionState,
    isSynced,
    currentLabId,
    documentName,
    sessionData,
    error,
    connect,
    disconnect,
  };
});
